import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import type { EventDto } from '../api/types.js';
import { eventsApi } from '../api/events.js';
import { schoolFullLabel } from '../lib/format.js';
import { useEventsQuery } from '../hooks/queries.js';
import { useSession } from '../store/session.js';
import { CountdownBadge } from '../components/CountdownBadge.js';
import { Card } from '../components/Card.js';
import { EmptyState } from '../components/EmptyState.js';
import { ErrorState } from '../components/ErrorState.js';
import { LatestFeeds } from '../components/LatestFeeds.js';
import { Loading } from '../components/Loading.js';
import { Tag } from '../components/Tag.js';

type TabKey = 'all' | 'mine' | 'common';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: '全部赛事' },
  { key: 'mine', label: '我校专属' },
  { key: 'common', label: '跨校共性' },
];

/** 列表页每个赛事卡的"最近里程碑"：来自该赛事 timeline 查询（daysLeft ≥ 0 里最近的一个）。 */
function NextMilestone({ event }: { event: EventDto }) {
  const timelineQuery = useQueries({
    queries: [
      {
        queryKey: ['event-timeline-mini', event.code],
        queryFn: () => eventsApi.timeline(event.code),
        staleTime: 60_000,
      },
    ],
  })[0];
  const milestones = timelineQuery.data?.milestones ?? [];
  const upcoming = milestones
    .filter((milestone) => milestone.daysLeft !== null && milestone.daysLeft >= 0)
    .sort((a, b) => (a.daysLeft ?? 0) - (b.daysLeft ?? 0))[0];

  if (!upcoming) {
    return <span className="muted small">暂无待办里程碑</span>;
  }
  return (
    <span className="row-tight">
      <span className="small">
        最近节点：{upcoming.label}（{upcoming.dueAt.slice(0, 10)}）
      </span>
      <CountdownBadge daysLeft={upcoming.daysLeft} />
    </span>
  );
}

function scopeBadge(event: EventDto) {
  if (event.scope === 'exclusive') {
    const school = (event.schools ?? [])[0];
    return (
      <Tag tone="brand" title="仅某校可参与">
        专属{school ? `· ${schoolFullLabel(school as never)}` : ''}
      </Tag>
    );
  }
  if (event.scope === 'common') {
    return <Tag tone="ok" title="多校共同参与">跨校共性</Tag>;
  }
  return null;
}

/**
 * 赛事联动列表页（F4，v0.3 竞赛资讯分类）：
 * 三档过滤——全部 / 我校专属（按当前用户学校）/ 跨校共性。卡片展示归属标识。
 */
export function EventsPage() {
  const user = useSession((state) => state.user);
  const [tab, setTab] = useState<TabKey>('all');
  const filter =
    tab === 'mine' && user ? { school: user.school } : tab === 'common' ? { scope: 'common' as const } : undefined;
  const eventsQuery = useEventsQuery(filter);

  if (eventsQuery.isPending) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-title">赛事联动</h1>
        </div>
        <Loading label="正在读取赛事列表…" rows={3} />
      </div>
    );
  }

  if (eventsQuery.isError) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-title">赛事联动</h1>
        </div>
        <ErrorState error={eventsQuery.error} title="读取赛事列表失败" onRetry={() => void eventsQuery.refetch()} />
      </div>
    );
  }

  const events = eventsQuery.data.events;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-title">赛事联动</h1>
          <p className="page-sub">
            重大赛事（挑战杯、国创等）的资讯、里程碑与备赛规划都聚合在这里，不用再逐个刷学校官网。
          </p>
        </div>
      </div>

      <Card title="最新竞赛资讯" subtitle="从各校官网与赛事官网自动同步，每 30 分钟更新一次">
        <LatestFeeds events={events} />
      </Card>

      <div className="tabs" role="tablist" aria-label="赛事分类">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={tab === item.key}
            className={tab === item.key ? 'tab tab--active' : 'tab'}
            onClick={() => setTab(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {events.length === 0 ? (
        <EmptyState
          icon="◇"
          title={tab === 'mine' ? '我校暂无专属赛事' : tab === 'common' ? '暂无跨校共性赛事' : '还没有赛事'}
          description="赛事方（organizer）在「设置与合规」里创建赛事并维护里程碑后，这里会列出全部赛事。"
        />
      ) : (
        <div className="grid-2">
          {events.map((event) => (
            <Link key={event.code} to={`/events/${event.code}`} className="card event-card">
              <div className="row-tight">
                <strong>{event.name}</strong>
                {scopeBadge(event)}
              </div>
              <div className="muted small">
                赛事码 {event.code}
                {event.season ? ` · ${event.season} 赛季` : ''}
                {typeof event.memberCount === 'number' ? ` · ${event.memberCount} 人已加入` : ''}
              </div>
              {event.schools && event.schools.length > 1 ? (
                <div className="muted small">参与学校：{event.schools.map((s) => schoolFullLabel(s as never)).join('、')}</div>
              ) : null}
              <NextMilestone event={event} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
